import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSiteContent } from "../contexts/SiteContentContext";
import FleurKintsugi from "../components/brand/FleurKintsugi";
import StoryHero from "../components/story/StoryHero";
import StoryTimeline from "../components/story/StoryTimeline";
import StoryChapter from "../components/story/StoryChapter";
import StoryMethod from "../components/story/StoryMethod";
import StoryMedals from "../components/story/StoryMedals";
import Reveal from "../components/story/Reveal";
import ShareButtons from "../components/ShareButtons";

const CHAPTER_COUNT = 9;
const IMAGE_AT = { 2: "story_secondary_image", 7: "story_tertiary_image" };
const METHOD_AFTER = 3;
const MEDALS_AT = 5;

function Ornament() {
  return (
    <div className="relative py-2 flex items-center justify-center gap-6" aria-hidden="true">
      <span className="h-px w-20 lg:w-36 bg-gradient-to-r from-transparent via-primary/60 to-primary/60" />
      <FleurKintsugi size={28} />
      <span className="h-px w-20 lg:w-36 bg-gradient-to-l from-transparent via-primary/60 to-primary/60" />
    </div>
  );
}

export default function MyStory() {
  const { c } = useSiteContent();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const chapters = Array.from({ length: CHAPTER_COUNT }, (_, i) => ({
    title: c(`story_ch${i}_title`, ""),
    body: c(`story_ch${i}_body`, ""),
    image: IMAGE_AT[i] ? c(IMAGE_AT[i], "") : "",
  })).filter((ch) => ch.title || ch.body);

  return (
    <div data-testid="story-page">
      <StoryHero
        name={c("story_name", "Éric Awono")}
        title={c("story_title", "")}
        quote={c("story_lead_quote", "")}
        portrait={c("story_portrait_image", "")}
      />

      <Ornament />
      <StoryTimeline raw={c("story_timeline", "")} />

      {chapters.map((ch, i) => (
        <div key={i}>
          {i > 0 && <Ornament />}
          <StoryChapter index={i} title={ch.title} body={ch.body} image={ch.image} imageAlt={ch.title}>
            {i === MEDALS_AT && (
              <StoryMedals
                medals={c("story_medals", "")}
                juries={[
                  { title: c("story_jury_title", ""), scores: c("story_jury_scores", ""), note: c("story_jury_note", "") },
                  { title: c("story_jury2_title", ""), scores: c("story_jury2_scores", ""), note: c("story_jury2_note", "") },
                ]}
              />
            )}
          </StoryChapter>
          {i === METHOD_AFTER && (
            <StoryMethod
              eyebrow={c("story_method_eyebrow", "La signature")}
              title={c("story_method_title", "")}
              intro={c("story_method_intro", "")}
              steps={c("story_method_steps", "")}
              tagline={c("story_method_tagline", "")}
              image={c("story_method_image", "")}
            />
          )}
        </div>
      ))}

      <Reveal className="relative max-w-4xl mx-auto px-6 py-20 lg:py-28 text-center" data-testid="story-cta">
        <div className="kintsugi-divider mb-10" />
        <p className="font-vibes molten-flow leading-[1.1] mb-8" style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)" }}>
          Venez goûter l'histoire.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link to="/boutique" className="btn-gold-marble inline-flex items-center gap-3 rounded-full" data-testid="story-cta-shop">
            Découvrir les créations <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/reservation-table" className="inline-flex items-center gap-2 rounded-full border border-foreground/30 hover:border-primary px-6 h-11 text-sm transition-colors" data-testid="story-cta-book">
            Réserver une table au bar
          </Link>
        </div>
        <ShareButtons sharePath="mon-histoire" title={`Mon histoire — ${c("story_name", "Éric Awono")} · Saveurs Normandes`} text={c("story_title", "")} className="justify-center mt-10" />
        <div className="kintsugi-divider mt-10" />
      </Reveal>
    </div>
  );
}
